// src/context/AdminContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  getCurrentAuthUser,
  subscribeToAuthChanges,
} from "../services/authDataService";

const AdminContext = createContext({
  isAdmin: false,
  isSuperAdmin: false,
  adminLoading: true,
  adminError: null,
  refreshAdminClaim: async () => false,
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function AdminProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [adminError, setAdminError] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetState = useCallback((fn) => {
    if (!mountedRef.current) return;
    fn();
  }, []);

  // ✅ Flags setzen
  const applyFlags = useCallback(({ adminFlag, superFlag }) => {
    const effectiveAdmin = adminFlag || superFlag;

    safeSetState(() => {
      setIsSuperAdmin(!!superFlag);
      setIsAdmin(!!effectiveAdmin);
      setAdminLoading(false);
      setAdminError(null);
    });

    return { isAdmin: !!effectiveAdmin, isSuperAdmin: !!superFlag };
  }, [safeSetState]);

  const clearFlags = useCallback(() => {
    safeSetState(() => {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setAdminLoading(false);
      setAdminError(null);
    });

    return { isAdmin: false, isSuperAdmin: false };
  }, [safeSetState]);

  const readClaims = useCallback(
    async (user, forceRefresh = false) => {
      if (!user) return clearFlags();

      safeSetState(() => {
        setAdminLoading(true);
        setAdminError(null);
      });

      try {
        const tokenResult = await user.getIdTokenResult(!!forceRefresh);

        const adminFlag = tokenResult?.claims?.admin === true;
        const superFlag = tokenResult?.claims?.superadmin === true;

        return applyFlags({ adminFlag, superFlag });
      } catch (e) {
        console.warn("Claims lesen fehlgeschlagen:", e);

        safeSetState(() => {
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setAdminLoading(false);
          setAdminError(e?.message || "admin-claim-read-failed");
        });

        return { isAdmin: false, isSuperAdmin: false };
      }
    },
    [applyFlags, clearFlags, safeSetState]
  );

  // ✅ Auth Listener (Web + Native kompatibel)
  useEffect(() => {
    let unsubscribeRef = null;
    let mounted = true;

    safeSetState(() => setAdminLoading(true));

    const init = async () => {
      try {
        const user = await getCurrentAuthUser();

        if (mounted) {
          await readClaims(user, false);
        }

        unsubscribeRef = await subscribeToAuthChanges(async (nextUser) => {
          if (!mounted) return;
          await readClaims(nextUser, false);
        });
      } catch (error) {
        console.warn("Admin auth listener failed:", error);
        if (mounted) clearFlags();
      }
    };

    init();

    return () => {
      mounted = false;

      if (typeof unsubscribeRef === "function") {
        const maybePromise = unsubscribeRef();
        if (maybePromise?.catch) maybePromise.catch(() => {});
      }
    };
  }, [readClaims, clearFlags, safeSetState]);

  // ✅ Manueller Refresh (z. B. nach Admin setzen)
  const refreshAdminClaim = useCallback(async () => {
    const user = await getCurrentAuthUser();

    if (!user) {
      clearFlags();
      return false;
    }

    safeSetState(() => {
      setAdminLoading(true);
      setAdminError(null);
    });

    const delays = [0, 800, 1600];

    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await sleep(delays[i]);

      try {
        await user.getIdToken(true);
        const result = await readClaims(user, true);
        return !!result;
      } catch (e) {
        if (i === delays.length - 1) {
          console.warn("refreshAdminClaim failed:", e);

          safeSetState(() => {
            setAdminLoading(false);
            setAdminError(e?.message || "admin-claim-refresh-failed");
          });

          return false;
        }
      }
    }

    return false;
  }, [clearFlags, readClaims, safeSetState]);

  const value = useMemo(
    () => ({
      isAdmin,
      isSuperAdmin,
      adminLoading,
      adminError,
      refreshAdminClaim,
    }),
    [isAdmin, isSuperAdmin, adminLoading, adminError, refreshAdminClaim]
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  return useContext(AdminContext);
}